import { notification } from 'antd';
import { addCMSMedia } from 'api/cms-media.api';
import { updateDestination } from 'api/destinations.api';
import { MediaSelector } from 'components/MediaSelector';
import { DestinationGeoType } from 'constants/destination-geo-type';
import {
    DESTINATION_MEDIA,
    DESTINATION_MEDIA_RATIO,
} from 'constants/media-sizes.constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import styles from './style.module.css';
import { PurchaseStockPhotoWarningModal } from 'components/StockPhotoWarning';
import { getSourceId } from 'api/sources.api';

type DestinationListImageProps = Readonly<{
    destination: DestinationSchema;
    onUpdate: () => void;
}>;

export function DestinationListImage({
    destination,
    onUpdate,
}: DestinationListImageProps) {
    const [imageToPurchase, setImageToPurchase] = useState<CMSMedia | null>(
        null,
    );
    const [currentImage, setCurrentImage] = useState<CMSMedia | null>(
        destination.cover_image ?? null,
    );

    const isCountry = destination.geo_type === DestinationGeoType.COUNTRY;

    const location: PSLocation | null = useMemo(() => {
        // For country destinations, use the country name as location
        if (isCountry && destination.name) {
            return {
                name: destination.name,
                countryCode: '',
                detail: destination.name,
                fullDetail: destination.name,
                lat: null,
                lon: null,
            };
        }

        // For locality destinations, use the locality
        return destination?.locality
            ? {
                  name: destination.locality ?? '',
                  countryCode: destination.country_code ?? '',
                  detail: destination.country ?? '',
                  fullDetail: destination.country ?? '',
                  lat: destination.lat ?? null,
                  lon: destination.lon ?? null,
              }
            : null;
    }, [destination, isCountry]);

    const handleSaveImage = useCallback(
        async (
            media: CMSMedia & {
                $$cropInfo?: CropInfo;
            },
        ) => {
            try {
                const savedMedia = media.id
                    ? media
                    : await addCMSMedia(media, DESTINATION_MEDIA);

                if (!savedMedia.id) {
                    throw new Error('Media is not added');
                }

                await updateDestination(destination.id, {
                    ...destination,
                    cover_image_id: savedMedia.id,
                });

                setCurrentImage(savedMedia);

                onUpdate();
            } catch (error) {
                notification.error({
                    message: 'Error updating destination image',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error occurred',
                });
            }
        },
        [destination, onUpdate],
    );

    const handleSelect = useCallback(
        async (
            media: CMSMedia & {
                $$cropInfo?: CropInfo;
            },
        ) => {
            const shutterstockSourceId = await getSourceId('shutterstock');
            if (media.source_id === shutterstockSourceId) {
                setImageToPurchase(media);
                return;
            }

            await handleSaveImage(media);
        },
        [handleSaveImage],
    );

    useEffect(() => {
        if (
            destination.cover_image &&
            destination.cover_image?.id !== currentImage?.id
        ) {
            setCurrentImage(destination.cover_image ?? null);
        }
    }, [destination.cover_image, currentImage]);

    return (
        <>
            <div className={styles.image}>
                <MediaSelector
                    initialMediaSource="UNSPLASH"
                    disallowedSources={['FAVORITES']}
                    location={location}
                    includeVideos={false}
                    onSelect={handleSelect}
                    media={currentImage}
                    ratio={DESTINATION_MEDIA_RATIO}
                    showPreviewPopover={true}
                    showCropperOnEdit={false}
                />
            </div>
            {imageToPurchase ? (
                <PurchaseStockPhotoWarningModal
                    photosToPurchase={[imageToPurchase]}
                    onAdd={async (purchasedPhotos) => {
                        await handleSaveImage(purchasedPhotos[0]);
                        setImageToPurchase(null);
                    }}
                    onCancel={() => {
                        setImageToPurchase(null);
                    }}
                    sizesToUpload={DESTINATION_MEDIA}
                />
            ) : null}
        </>
    );
}
